import React, { createContext, useState, useEffect, useCallback } from 'react'
import { ThemeProvider as StyledThemeProvider } from 'styled-components'
import { ThemeProvider } from '@material-ui/core'
import {
  IEntity,
  IAuth,
  ICookiesUser,
  ICoreContext,
  ICoreProvider,
  IUserFunction,
  IToken,
  ICompany,
} from 'interfaces'
import DefaultEntity from 'app/Default'
import CompanyEntity from 'app/Company'
import EmployeeEntity from 'app/Employee'
import WorkerEntity from 'app/Worker'
import CorbanCEPEntity from 'app/Corban/CEP'
import LegalPersonEntity from 'app/LegalPerson'
import Cookies, { getCookies } from '../CookiesProvider'
import SalesChannelEntity from 'app/SalesChannel'
import { specificRoutes } from 'app/SalesChannel/routes'
import { specificRoutes as specificRoutesCompany } from 'app/Company/routes'
import { COMPANY_SUBMODULES, SALESCHANNEL_SUBMODULES } from 'interfaces/submodules'
import SelfAttendanceEntity from 'app/SelfAttendance'

export const CoreContext = createContext<ICoreContext>({} as ICoreContext)

const cookie = getCookies('CFC') as IAuth
export let getToken: IToken = {
  token: cookie.token_jwt,
  refreshToken: cookie.refresh,
  showModalDebits: cookie.showModalDebits,
  isHiddenButton: cookie.isHiddenButton,
}
export const setToken = (prop: IToken) => {
  getToken = prop
}

let storedEntity = getCookies('CFU') as ICookiesUser
export let getUserFunction = {
  userFunction: storedEntity.userFunction,
}
export const setUserFunction = (userFunction: string) => {
  getUserFunction = {
    userFunction,
  }
}

export const clearCookies = () => {
  Cookies.remove('CFC')
  Cookies.remove('CFU')
}

const getRoute = (subModule: string, selectedEntity: IEntity) => {
  let route = selectedEntity.route
  if (subModule === COMPANY_SUBMODULES.SUPERVISOR) route = specificRoutesCompany['SUPERVISOR']
  if (subModule === SALESCHANNEL_SUBMODULES.CDV) route = specificRoutes['CDCVEICULO']
  if (subModule === SALESCHANNEL_SUBMODULES.CP && process.env.REACT_APP_CLIENT?.includes('ALFA'))
    route = specificRoutes['FGTS_ALFA']
  if (subModule === SALESCHANNEL_SUBMODULES.CP && process.env.REACT_APP_CLIENT?.includes('PEFISA'))
    route = specificRoutes['FGTS_PEFISA']
  return route
}

const entities = [
  CompanyEntity,
  DefaultEntity,
  EmployeeEntity,
  WorkerEntity,
  SalesChannelEntity,
  CorbanCEPEntity,
  LegalPersonEntity,
  SelfAttendanceEntity,
]
const CoreProvider: React.FC<ICoreProvider> = ({ children }) => {
  const storedEntity = getCookies('CFU') as ICookiesUser
  const selectedEntity = entities.find((obj) => obj.name === storedEntity.entityName)
  const selectedFunctions = localStorage.getItem('selectedFunctions')
    ? JSON.parse(localStorage.getItem('selectedFunctions') as string)
    : []
  const selectedCompany = localStorage.getItem('selectedCompany')
    ? (JSON.parse(localStorage.getItem('selectedCompany') as string) as ICompany)
    : ({} as ICompany)

  const [entity, setEntity] = useState<IEntity>(() => {
    if (selectedEntity) {
      const foundFunction = selectedFunctions.find(
        ({ id }: { id: string }) => id === storedEntity.userFunction
      )

      // TODO: Extrair para função e tipar corretamente a variável selectedFunctions
      let isCompanyPrivate = selectedCompany?.empresa_privada
      if (!Object.keys(selectedCompany).length && foundFunction) {
        if (!!foundFunction?.entidade?.informacoes?.empresa)
          isCompanyPrivate = foundFunction?.entidade?.informacoes?.empresa?.empresa_privada
        else
          isCompanyPrivate =
            foundFunction?.entidade?.informacoes?.empresas?.length > 0
              ? foundFunction?.entidade?.informacoes?.empresas[0]?.empresa_privada
              : undefined
      }

      return {
        ...selectedEntity,
        funcoes: selectedFunctions,
        funcao: foundFunction,
        module: {
          mainModule: storedEntity?.mainModule,
          subModule: storedEntity?.subModule,
        },
        company: selectedCompany,
        isCompanyPrivate,
        route: getRoute(storedEntity.subModule as string, selectedEntity),
      }
    }
    return DefaultEntity
  })
  const [auth, setAuth] = useState<IAuth>(getCookies('CFC'))
  const [functions, setFunctions] = useState<IUserFunction[]>(
    localStorage.getItem('functions')
      ? (JSON.parse(localStorage.getItem('functions') || '') as IUserFunction[])
      : []
  )

  const resetEntity = useCallback(() => {
    setEntity(DefaultEntity)
    Cookies.remove('CFU')
    localStorage.clear()
  }, [setEntity])
  const logout = useCallback(() => {
    getToken = {} as IToken
    localStorage.clear()
    clearCookies()
    setAuth({} as IAuth)
    setEntity(DefaultEntity)
  }, [])

  // TODO: Passar para um reducer para não ficar compartilhando multiplos states e sim um dispatch
  const values: ICoreContext = {
    auth,
    entity,
    functions,
    setFunctions,
    setEntity,
    setAuth,
    logout,
    resetEntity,
  }

  useEffect(() => {
    if (auth.token_jwt) {
      getToken = {
        token: auth.token_jwt,
        refreshToken: auth.refresh,
        showModalDebits: auth.showModalDebits,
        isHiddenButton: auth.isHiddenButton,
      } as IToken
    }
    Cookies.set('CFC', auth, { expires: 365, sameSite: 'strict' })
  }, [auth])

  useEffect(() => {
    const cookieUser: ICookiesUser = {
      entityName: entity.name,
      userFunction: entity.funcao?.id,
      mainModule: entity.module?.mainModule,
      subModule: entity.module?.subModule,
    }
    if (cookieUser.userFunction) {
      getUserFunction = {
        userFunction: entity.funcao?.id,
      }
      if (entity.funcoes) localStorage.setItem('selectedFunctions', JSON.stringify(entity.funcoes))
      if (entity.company) localStorage.setItem('selectedCompany', JSON.stringify(entity.company))
      Cookies.set('CFU', cookieUser, { expires: 365, sameSite: 'strict' })
    }
  }, [entity])

  useEffect(() => {
    if (functions.length > 0) {
      localStorage.setItem('functions', JSON.stringify(functions))
    }
  }, [functions])

  return (
    <CoreContext.Provider value={values}>
      <ThemeProvider theme={entity.materialTheme}>
        <StyledThemeProvider theme={entity.theme}>{children}</StyledThemeProvider>
      </ThemeProvider>
    </CoreContext.Provider>
  )
}

export default CoreProvider
